/**
 * AlertsPanelV2 Component
 *
 * Displays grouped performance alerts for the SPLH Dashboard V2.
 * Alerts are organized by metric (SPLH, Sales, Labor %, Hours) in accordion structure.
 *
 * Features:
 * - Accordion structure grouped by metric type
 * - Severity counts (critical/warning) in headers
 * - Color-coded alert cards with comparison delta
 * - Expandable sections with individual alerts
 * - Summary chips for quick overview
 *
 * @module pages/common/dashboards/SPLHDashboard/components/AlertsPanelV2
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Skeleton,
  Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Error as CriticalIcon,
  Warning as WarningIcon,
  CheckCircle as NoAlertsIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { formatSPLH, formatCurrency, formatPercentage, formatNumber } from '../../utils/formatUtils';

/**
 * Metric configuration with display settings
 * @constant {Object}
 */
const METRIC_CONFIG = {
  splh: {
    label: 'SPLH',
    icon: '📊',
    format: formatSPLH,
    upIsGood: true,
  },
  sales: {
    label: 'Sales',
    icon: '💰',
    format: (v) => formatCurrency(v, { minimumFractionDigits: 0 }),
    upIsGood: true,
  },
  laborPct: {
    label: 'Labor %',
    icon: '👥',
    format: (v) => formatPercentage(v / 100, { decimalPlaces: 1 }),
    upIsGood: false,
  },
  hours: {
    label: 'Hours',
    icon: '⏰',
    format: (v) => formatNumber(v, { decimalPlaces: 1 }),
    upIsGood: null, // Context-dependent
  },
};

/**
 * Severity configuration
 * @constant {Object}
 */
const SEVERITY_CONFIG = {
  CRITICAL: {
    color: '#F44336',
    bgColor: '#FFEBEE',
    borderColor: '#EF9A9A',
    icon: CriticalIcon,
    label: 'Critical',
  },
  WARNING: {
    color: '#FF9800',
    bgColor: '#FFF3E0',
    borderColor: '#FFB74D',
    icon: WarningIcon,
    label: 'Warning',
  },
};

/**
 * Compact individual alert row component (single line)
 */
const AlertCard = ({ alert, metricConfig }) => {
  const severityConfig = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.WARNING;
  const SeverityIcon = severityConfig.icon;

  const isNegative = alert.delta < 0;
  const TrendIcon = isNegative ? TrendingDownIcon : TrendingUpIcon;
  const trendColor = metricConfig.upIsGood !== null
    ? (isNegative === !metricConfig.upIsGood ? '#10B981' : '#EF4444')
    : '#6B7280';

  const deltaSign = alert.deltaPct >= 0 ? '+' : '';

  return (
    <Box
      sx={{
        py: 0.75,
        px: 1.5,
        mb: 0.5,
        borderRadius: 1,
        bgcolor: severityConfig.bgColor,
        borderLeft: `3px solid ${severityConfig.color}`,
        '&:last-child': { mb: 0 },
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {/* Column 1: Severity icon */}
      <SeverityIcon sx={{ color: severityConfig.color, fontSize: 16 }} />

      {/* Column 2: Entity name with parent context */}
      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {alert.entityName}
        {alert.parentEntityName && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5, fontWeight: 400 }}>
            ({alert.parentEntityName})
          </Typography>
        )}
      </Typography>

      {/* Column 3: Metrics - current value, comparison, delta */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography variant="body2" fontWeight={600}>
          {metricConfig.format(alert.currentValue)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          vs {metricConfig.format(alert.comparisonValue)}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <TrendIcon sx={{ fontSize: 14, color: trendColor }} />
          <Typography variant="caption" sx={{ color: trendColor, fontWeight: 600 }}>
            {deltaSign}{alert.deltaPct?.toFixed(1)}%
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

AlertCard.propTypes = {
  alert: PropTypes.shape({
    id: PropTypes.string.isRequired,
    metric: PropTypes.string.isRequired,
    severity: PropTypes.string.isRequired,
    entityName: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    currentValue: PropTypes.number,
    comparisonValue: PropTypes.number,
    delta: PropTypes.number,
    deltaPct: PropTypes.number,
    parentEntityName: PropTypes.string,
  }).isRequired,
  metricConfig: PropTypes.object.isRequired,
};

/**
 * Metric accordion section component
 */
const MetricAccordion = ({ metricKey, metricGroup, expanded, onToggle, loading }) => {
  const config = METRIC_CONFIG[metricKey];
  const criticalAlerts = metricGroup?.critical || [];
  const warningAlerts = metricGroup?.warning || [];
  const totalAlerts = criticalAlerts.length + warningAlerts.length;
  const criticalCount = criticalAlerts.length;
  const warningCount = warningAlerts.length;

  // Don't render if no alerts
  if (!loading && totalAlerts === 0) {
    return null;
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      sx={{
        mb: 1,
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        borderRadius: '8px !important',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: '0 0 8px 0' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 48,
          '&.Mui-expanded': { minHeight: 48 },
          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5 },
        }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 100 }}>
          {config.icon} {config.label}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, ml: 'auto', mr: 1 }}>
          {criticalCount > 0 && (
            <Chip
              icon={<CriticalIcon sx={{ fontSize: 14 }} />}
              label={criticalCount}
              size="small"
              sx={{
                bgcolor: SEVERITY_CONFIG.CRITICAL.bgColor,
                color: SEVERITY_CONFIG.CRITICAL.color,
                fontWeight: 600,
                height: 24,
                '& .MuiChip-icon': { color: SEVERITY_CONFIG.CRITICAL.color },
              }}
            />
          )}
          {warningCount > 0 && (
            <Chip
              icon={<WarningIcon sx={{ fontSize: 14 }} />}
              label={warningCount}
              size="small"
              sx={{
                bgcolor: SEVERITY_CONFIG.WARNING.bgColor,
                color: SEVERITY_CONFIG.WARNING.color,
                fontWeight: 600,
                height: 24,
                '& .MuiChip-icon': { color: SEVERITY_CONFIG.WARNING.color },
              }}
            />
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0 }}>
        {loading ? (
          <Stack spacing={1}>
            {[1, 2].map((i) => (
              <Skeleton key={i} variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
            ))}
          </Stack>
        ) : (
          <>
            {criticalAlerts.map((alert) => (
              <AlertCard key={alert.id} alert={alert} metricConfig={config} />
            ))}
            {warningAlerts.map((alert) => (
              <AlertCard key={alert.id} alert={alert} metricConfig={config} />
            ))}
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

MetricAccordion.propTypes = {
  metricKey: PropTypes.string.isRequired,
  metricGroup: PropTypes.shape({
    critical: PropTypes.array,
    warning: PropTypes.array,
  }),
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

/**
 * Empty state component
 */
const EmptyState = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 4,
      bgcolor: '#f0fdf4',
      borderRadius: 1,
      border: '1px solid #bbf7d0',
    }}
  >
    <NoAlertsIcon sx={{ fontSize: 40, color: '#10B981', mb: 1 }} />
    <Typography variant="body1" color="text.secondary">
      No alerts
    </Typography>
    <Typography variant="caption" color="text.secondary">
      All locations are performing within expected ranges
    </Typography>
  </Box>
);

/**
 * AlertsPanelV2 Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.alerts - Grouped alerts from backend V2 API
 * @param {boolean} [props.loading=false] - Whether data is loading
 * @returns {React.Component} AlertsPanelV2 component
 */
const AlertsPanelV2 = ({ alerts, loading = false }) => {
  // Track expanded accordions - start with all collapsed for quick overview
  const [expandedSections, setExpandedSections] = useState({});

  // Calculate total alerts count
  const totalAlerts = useMemo(() => {
    if (!alerts) return { total: 0, critical: 0, warning: 0 };

    let critical = 0;
    let warning = 0;

    Object.values(alerts).forEach((group) => {
      critical += group?.critical?.length || 0;
      warning += group?.warning?.length || 0;
    });

    return { total: critical + warning, critical, warning };
  }, [alerts]);

  // Check if any accordion would render
  const hasAlerts = useMemo(() => {
    if (!alerts) return false;
    return Object.keys(METRIC_CONFIG).some((key) => {
      const group = alerts[key];
      return (group?.critical?.length || 0) + (group?.warning?.length || 0) > 0;
    });
  }, [alerts]);

  const handleToggle = (metricKey) => () => {
    setExpandedSections((prev) => ({
      ...prev,
      [metricKey]: !prev[metricKey],
    }));
  };

  // Loading state
  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Skeleton variant="rounded" width={80} height={24} />
          <Skeleton variant="rounded" width={80} height={24} />
        </Box>
        {Object.keys(METRIC_CONFIG).map((key) => (
          <Skeleton key={key} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  // Empty state
  if (!hasAlerts) {
    return <EmptyState />;
  }

  return (
    <Box>
      {/* Summary header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Total Alerts:
        </Typography>
        {totalAlerts.critical > 0 && (
          <Chip
            icon={<CriticalIcon sx={{ fontSize: 14 }} />}
            label={`${totalAlerts.critical} Critical`}
            size="small"
            sx={{
              bgcolor: SEVERITY_CONFIG.CRITICAL.bgColor,
              color: SEVERITY_CONFIG.CRITICAL.color,
              fontWeight: 500,
              '& .MuiChip-icon': { color: SEVERITY_CONFIG.CRITICAL.color },
            }}
          />
        )}
        {totalAlerts.warning > 0 && (
          <Chip
            icon={<WarningIcon sx={{ fontSize: 14 }} />}
            label={`${totalAlerts.warning} Warning`}
            size="small"
            sx={{
              bgcolor: SEVERITY_CONFIG.WARNING.bgColor,
              color: SEVERITY_CONFIG.WARNING.color,
              fontWeight: 500,
              '& .MuiChip-icon': { color: SEVERITY_CONFIG.WARNING.color },
            }}
          />
        )}
      </Box>

      {/* Metric accordions */}
      {Object.keys(METRIC_CONFIG).map((metricKey) => (
        <MetricAccordion
          key={metricKey}
          metricKey={metricKey}
          metricGroup={alerts?.[metricKey]}
          expanded={expandedSections[metricKey] || false}
          onToggle={handleToggle(metricKey)}
          loading={loading}
        />
      ))}
    </Box>
  );
};

AlertsPanelV2.propTypes = {
  /** Grouped alerts from backend V2 API */
  alerts: PropTypes.shape({
    splh: PropTypes.shape({
      critical: PropTypes.array,
      warning: PropTypes.array,
    }),
    sales: PropTypes.shape({
      critical: PropTypes.array,
      warning: PropTypes.array,
    }),
    laborPct: PropTypes.shape({
      critical: PropTypes.array,
      warning: PropTypes.array,
    }),
    hours: PropTypes.shape({
      critical: PropTypes.array,
      warning: PropTypes.array,
    }),
  }),
  /** Whether data is loading */
  loading: PropTypes.bool,
};

export default AlertsPanelV2;
